import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Zap, CloudLightning, Radio, ShieldCheck, Smartphone, Monitor,
  Bot, Wrench, Headphones, Apple, Check, MapPin, Clock, Hammer, Home,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LiveTicker from "@/components/LiveTicker";
import FindAPro from "@/components/FindAPro";
import StormReadinessHub from "@/components/StormReadinessHub";
import FoundingNetwork from "@/components/FoundingNetwork";
import FoundingMembers from "@/components/FoundingMembers";
import RequestDemo from "@/components/RequestDemo";
import api, { formatApiError } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1605727216801-e27ce1d0cc28?auto=format&fit=crop&w=1920&q=80"; // dramatic lightning supercell
const STORM_RADAR = "https://images.unsplash.com/photo-1561484930-998b6a7b22e8?auto=format&fit=crop&w=1600&q=80"; // dark stormy sky
const TECH_FIELD = "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1600&q=80"; // technician/tools
const HOME_DAMAGE = "https://images.unsplash.com/photo-1518780664697-55e3ad937233?auto=format&fit=crop&w=1600&q=80"; // home/architecture
const PHONE_MOCK = "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80"; // phone in hand

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="block w-8 h-px bg-[#dc2626]" />
      <span className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-400">{children}</span>
    </div>
  );
}

function Capability({ title, kicker, children, image, span = "md:col-span-6", height = "min-h-[360px]" }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      className={`relative ${span} ${height} group overflow-hidden border border-white/5 bg-[#121214]`}
    >
      {image && (
        <div className="absolute inset-0 opacity-40 group-hover:opacity-60 transition-opacity duration-700">
          <img src={image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/70 to-transparent" />
        </div>
      )}
      <div className="relative z-10 h-full p-8 flex flex-col justify-end">
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-[#dc2626] mb-3">
          {kicker}
        </div>
        <h3 className="font-display text-3xl md:text-4xl text-white leading-[1.05] mb-4 tracking-tight">
          {title}
        </h3>
        <p className="font-body text-sm md:text-base text-zinc-300 max-w-md leading-relaxed">{children}</p>
      </div>
    </motion.div>
  );
}

function PlatformItem({ href, icon: I, label, testid }) {
  const content = <><I size={12} /> {label}</>;
  if (!href) return <span className="flex items-center gap-1.5">{content}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid={testid}>
      {content}
    </a>
  );
}

function StoreBadges({ appStore, googlePlay, web, iosNote, slug }) {
  const ext = { target: "_blank", rel: "noopener noreferrer" };
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4" data-testid={`store-badges-${slug}`}>
      {appStore && (
        <a href={appStore} {...ext} className="block" data-testid={`badge-app-store-${slug}`}>
          <img src="/brand/app-store-badge.svg" alt="Download on the App Store" className="h-[40px] w-auto" />
        </a>
      )}
      {googlePlay && (
        <a href={googlePlay} {...ext} className="block h-[40px] overflow-hidden" data-testid={`badge-google-play-${slug}`}>
          <img src="/brand/google-play-badge.png" alt="Get it on Google Play" className="h-[60px] w-auto max-w-none" style={{ margin: "-10px -10px" }} />
        </a>
      )}
      {iosNote && <span className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid={`ios-note-${slug}`}>{iosNote}</span>}
      {web && (
        <a href={web} {...ext} className="w-full font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors underline underline-offset-4" data-testid={`open-web-app-${slug}`}>
          Open Web App
        </a>
      )}
    </div>
  );
}

function AppCard({ name, kicker, blurb, Icon, accent, idx, onWaitlist, badge, logo, logoScale = 1.71, logoFit = "cover", status = "Coming soon", links = {}, iosLabel = "iOS", waitlist = true, store }) {
  const slug = name.toLowerCase().replace(/\s+/g, "-");
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "" });
    try {
      await onWaitlist({ email, app: name });
      setState({ loading: false, ok: true, error: "" });
      setEmail("");
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <motion.div
      variants={fadeUp}
      custom={idx}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className="relative border border-white/5 bg-[#121214] p-7 flex flex-col group hover:border-white/15 transition-colors"
      data-testid={`app-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="absolute top-0 right-0 flex items-stretch">
        {badge && (
          <div className="px-3 py-1 bg-white text-[#0a0a0b] font-mono-apex text-[9px] uppercase tracking-[0.25em]">
            {badge}
          </div>
        )}
        <div className="px-3 py-1 bg-[#dc2626] font-mono-apex text-[9px] uppercase tracking-[0.25em] text-white">
          {status}
        </div>
      </div>
      <div
        className="w-full mb-6 flex items-center justify-center"
        style={{ minHeight: logo ? "unset" : "auto" }}
      >
        {logo ? (
          <div className="relative w-full bg-[#0a0a0b] border border-white/5 overflow-hidden flex items-center justify-center" style={{ aspectRatio: "4 / 3" }}>
            <img
              src={logo}
              alt={`${name} logo`}
              style={{ width: "100%", height: "100%", objectFit: logoFit, transform: `scale(${logoScale})` }}
              data-testid={`app-card-logo-${name.toLowerCase().replace(/\s+/g, "-")}`}
              loading="lazy"
            />
          </div>
        ) : (
          <div
            className="w-full bg-[#0a0a0b] border border-white/5 flex items-center justify-center"
            style={{ aspectRatio: "4 / 3" }}
          >
            <div
              className="w-12 h-12 flex items-center justify-center border"
              style={{ borderColor: accent + "60", background: accent + "12" }}
            >
              <Icon size={22} strokeWidth={1.5} style={{ color: accent }} />
            </div>
          </div>
        )}
      </div>
      <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">{kicker}</div>
      <h3 className="font-display text-3xl text-white tracking-tight mb-3 leading-tight">{name}</h3>
      <p className="font-body text-sm text-zinc-400 leading-relaxed mb-6 flex-1">{blurb}</p>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-6 font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        <PlatformItem href={links.ios} icon={Apple} label={iosLabel} testid={`app-link-ios-${slug}`} />
        <span className="text-zinc-700">·</span>
        <PlatformItem href={links.android} icon={Smartphone} label="Android" testid={`app-link-android-${slug}`} />
        <span className="text-zinc-700">·</span>
        <PlatformItem href={links.web} icon={Monitor} label="Web" testid={`app-link-web-${slug}`} />
      </div>

      {store && <StoreBadges {...store} slug={slug} />}

      {waitlist && <form onSubmit={submit} className="space-y-2">
        <div className="flex gap-2">
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="input-apex flex-1 px-3 py-2.5 font-body text-sm min-w-0"
            data-testid={`waitlist-email-${name.toLowerCase().replace(/\s+/g, "-")}`}
          />
          <button
            type="submit"
            disabled={state.loading}
            className="btn-primary-apex px-4 py-2.5 font-mono-apex text-[10px] uppercase tracking-[0.2em] disabled:opacity-60 whitespace-nowrap"
            data-testid={`waitlist-submit-${name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {state.loading ? "…" : "Notify me"}
          </button>
        </div>
        {state.error && <div className="font-mono-apex text-[10px] text-[#dc2626]">{state.error}</div>}
        {state.ok && <div className="font-mono-apex text-[10px] text-emerald-400" data-testid={`waitlist-success-${name.toLowerCase().replace(/\s+/g, "-")}`}>You're on the list. We'll be in touch.</div>}
      </form>}
    </motion.div>
  );
}

export default function Landing() {
  const [contact, setContact] = useState({ name: "", email: "", company: "", company_size: "", intent: "", message: "" });
  const [contactState, setContactState] = useState({ loading: false, ok: false, error: "" });
  const [audience, setAudience] = useState("homeowner"); // 'homeowner' | 'contractor'

  const submitContact = async (e) => {
    e.preventDefault();
    setContactState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/contact", contact);
      setContactState({ loading: false, ok: true, error: "" });
      setContact({ name: "", email: "", company: "", company_size: "", intent: "", message: "" });
    } catch (err) {
      setContactState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  const joinWaitlist = async ({ email, app }) => {
    await api.post("/waitlist", { email, app });
  };

  return (
    <div className="bg-[#0a0a0b] text-white min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0b]/40 via-[#0a0a0b]/30 to-[#0a0a0b]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b]/85 via-transparent to-[#0a0a0b]/40" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pb-24 pt-40">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="flex items-center gap-3 mb-8">
            <span className="live-dot" />
            <span className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-300">
              live storm watch · 14 active threats · q1 2026
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="font-display text-[14vw] md:text-[10vw] lg:text-[8.5rem] leading-[0.92] tracking-tight max-w-6xl"
          >
            <span className="silver-text">Restoration,</span><br />
            <span className="text-white italic">at the speed of AI.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="font-body text-base md:text-xl text-zinc-300 max-w-2xl mt-8 leading-relaxed"
          >
            Storm, fire, water, mold, appliance — every kind of home damage, met with predictive
            intelligence, instant dispatch, and AI-assisted claims. Built for homeowners who need
            help, and for contractors building the future of the trade.
          </motion.p>

          {/* Dual audience selector */}
          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="mt-10 inline-flex border border-white/15 bg-[#0a0a0b]/70 backdrop-blur-sm"
            data-testid="audience-toggle"
          >
            {[
              { key: "homeowner", label: "I'm a Homeowner" },
              { key: "contractor", label: "I'm a Contractor" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setAudience(opt.key)}
                className={`px-6 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em] transition-all ${
                  audience === opt.key
                    ? "bg-[#dc2626] text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
                data-testid={`audience-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={4}
            className="flex flex-wrap gap-4 mt-6"
          >
            {audience === "homeowner" ? (
              <>
                <a
                  href="#find-a-pro"
                  className="inline-flex items-center gap-3 px-7 py-4 btn-primary-apex font-mono-apex text-[11px] uppercase tracking-[0.25em]"
                  data-testid="hero-cta-homeowner-primary"
                >
                  Find a certified pro near me <ArrowRight size={14} />
                </a>
                <a
                  href="#readiness"
                  className="inline-flex items-center gap-3 px-7 py-4 btn-secondary-apex font-mono-apex text-[11px] uppercase tracking-[0.25em]"
                  data-testid="hero-cta-homeowner-secondary"
                >
                  Storm readiness hub
                </a>
              </>
            ) : (
              <>
                <a
                  href="#join-network"
                  className="inline-flex items-center gap-3 px-7 py-4 btn-primary-apex font-mono-apex text-[11px] uppercase tracking-[0.25em]"
                  data-testid="hero-cta-contractor-primary"
                >
                  Apply to the founding network <ArrowRight size={14} />
                </a>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-3 px-7 py-4 btn-secondary-apex font-mono-apex text-[11px] uppercase tracking-[0.25em]"
                  data-testid="hero-cta-contractor-secondary"
                >
                  Book a demo
                </a>
              </>
            )}
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={5}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl"
          >
            {[
              { v: "<8 min", l: "Avg. dispatch to door" },
              { v: "$2.4B", l: "Claims processed" },
              { v: "92,000+", l: "Restoration jobs run" },
              { v: "24 / 7", l: "Storm watch operating" },
            ].map((s, i) => (
              <div key={i} className="border-l border-white/15 pl-4">
                <div className="font-mono-apex text-2xl md:text-3xl text-white">{s.v}</div>
                <div className="font-mono-apex text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2">
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <LiveTicker />

      <FindAPro />

      <StormReadinessHub />

      {/* APPS — the suite */}
      <section id="apps" className="relative py-28 md:py-36" data-testid="apps-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>01 · the apex suite</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
            <h2 className="font-display text-5xl md:text-7xl text-white tracking-tight max-w-3xl leading-[0.95]">
              Five apps. <span className="silver-text italic">One restoration command line.</span>
            </h2>
            <p className="font-body text-base text-zinc-400 max-w-md">
              Built ground-up for the operators, technicians, and homeowners who deal with what the
              weather leaves behind. Join the waitlist — first access goes out as each app ships.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <AppCard
              idx={0}
              name="APEX Response Management"
              kicker="for homeowners & property managers"
              accent="#dc2626"
              Icon={Headphones}
              badge="Be Apex Ready!"
              logo="/brand/apex-response-management.png?v=2"
              blurb="Manage inbound storm-damage calls, route crews to the highest-priority jobs, track every claim from first call to final invoice. The mission-control desk for restoration ops."
              store={{ googlePlay: "https://play.google.com/store/apps/details?id=com.apexinfinityventures.responsemanagement", iosNote: "iOS – Pending" }}
              onWaitlist={joinWaitlist}
            />
            <AppCard
              idx={1}
              name="APEX Community Pro"
              kicker="for property owners & managers"
              accent="#dc2626"
              Icon={Home}
              status="Live now"
              logo="/brand/community-pro-logo.png?v=1"
              logoFit="contain"
              logoScale={1.1}
              blurb="Built for property owners and managers of apartment communities and commercial properties. Report an urgent issue, dispatch a verified contractor in minutes, and track every property, unit, and repair from one secure dashboard."
              links={{
                ios: "https://apps.apple.com/app/id6813903293",
                android: "https://play.google.com/store/apps/details?id=com.apexinfinityventures.communitypro",
                web: "https://apexresponsemgmt.com",
              }}
              store={{
                appStore: "https://apps.apple.com/app/id6813903293",
                googlePlay: "https://play.google.com/store/apps/details?id=com.apexinfinityventures.communitypro",
                web: "https://apexresponsemgmt.com",
              }}
              waitlist={false}
            />
            <AppCard
              idx={2}
              name="APEX FieldProIQ"
              kicker="for the field"
              accent="#e8e8e8"
              Icon={Wrench}
              logo="/brand/apex-fieldproiq.png?v=2"
              blurb="Job assignments, before/after photo capture, customer e-signatures, time tracking, parts and material logs — built for the truck, the basement, and the rooftop."
              onWaitlist={joinWaitlist}
            />
            <AppCard
              idx={3}
              name="APEX Business Intelligence"
              kicker="the command center"
              accent="#dc2626"
              Icon={Bot}
              logo="/brand/apex-business-intelligence.png?v=1"
              logoFit="contain"
              logoScale={0.95}
              blurb="Apex Intelligence gives contractors and management teams a real-time view of the business through one smart, streamlined dashboard. From performance and operations to financial visibility, it delivers one-click reporting and real-time insight into every dollar moving through your operation."
              onWaitlist={joinWaitlist}
            />
            <AppCard
              idx={4}
              name="APEX IntelliVoice Rapid Response"
              kicker="24/7 AI call center"
              accent="#dc2626"
              Icon={Headphones}
              logo="/brand/apex-intellivoice.png?v=2"
              blurb="An AI-powered restoration call center that never misses a damage call — day, night, weekends, or holidays. It instantly triages the severity, captures the full property file, and routes emergencies to the nearest available crew in seconds. Faster response. Smarter solutions. Better outcomes. And when a personal touch matters most, a live human is always standing by."
              onWaitlist={joinWaitlist}
            />
          </div>
        </div>
      </section>

      {/* CAPABILITIES bento */}
      <section id="capabilities" className="relative py-28 md:py-36 border-t border-white/5" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>02 · the system</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
            <h2 className="font-display text-5xl md:text-7xl text-white tracking-tight max-w-3xl leading-[0.95]">
              Watch the sky. <span className="silver-text italic">Move the trucks.</span>
            </h2>
            <p className="font-body text-base text-zinc-400 max-w-md">
              Storm intelligence, dispatch, technician mobility, and claims — wired into one
              real-time fabric so the right crew is at the right home before insurance even calls.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <Capability
              kicker="01 · Storm Intelligence"
              title="See the storm before the siren."
              image={STORM_RADAR}
              span="md:col-span-7"
              height="min-h-[480px]"
            >
              Hyperlocal radar, NWS alert fusion, hail tracks, wind corridors, and predictive
              damage models — surfaced as ranked job-zones with confidence scores, hours before
              the calls start coming in.
            </Capability>

            <Capability
              kicker="02 · Dispatch"
              title="Closest crew, fastest answer."
              image={TECH_FIELD}
              span="md:col-span-5"
              height="min-h-[480px]"
            >
              Auto-route the nearest qualified technician with the right equipment, ETA visible
              to the homeowner, paper-trail visible to the adjuster.
            </Capability>

            <Capability
              kicker="03 · Claims & Documentation"
              title="The paperwork pays itself."
              image={HOME_DAMAGE}
              span="md:col-span-5"
              height="min-h-[420px]"
            >
              AI-assisted scope of work, photo evidence stitched to GPS and time, Xactimate-ready
              exports, and one-tap submission to the carrier.
            </Capability>

            <Capability
              kicker="04 · AI Sentinel"
              title="A weather analyst, on tap."
              span="md:col-span-7"
              height="min-h-[420px]"
            >
              Powered by Claude — interrogate your job board in natural language, generate damage
              forecasts, draft estimates, and stress-test capacity against a hurricane track.
            </Capability>
          </div>

          {/* Feature row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            {[
              { Icon: CloudLightning, t: "Hyperlocal storm fusion" },
              { Icon: Radio, t: "Realtime crew dispatch" },
              { Icon: ShieldCheck, t: "SOC2 grade security" },
              { Icon: Hammer, t: "Built for the trade" },
            ].map(({ Icon, t }, i) => (
              <div key={i} className="border border-white/5 bg-[#121214] p-6 flex items-center gap-4">
                <Icon size={22} strokeWidth={1.5} className="text-[#dc2626]" />
                <span className="font-mono-apex text-[11px] uppercase tracking-[0.2em] text-zinc-200">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FoundingMembers />

      <FoundingNetwork />

      <RequestDemo />

      {/* HOW IT WORKS */}
      <section id="how" className="relative py-28 md:py-36 border-t border-white/5" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>03 · operating doctrine</SectionLabel>
          <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight max-w-3xl leading-[0.95] mb-20">
            From storm cell to <em className="silver-text not-italic italic">signed work order</em> in three movements.
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-white/5">
            {[
              { n: "01", t: "Detect", d: "Apex Sentinel watches NWS, radar, hail tracks, and wind corridors 24/7. The moment a damage event is probable, your dispatch queue lights up with the highest-impact zones." },
              { n: "02", t: "Deploy", d: "Closest qualified crew is auto-routed, the homeowner gets a live ETA, the adjuster gets a heads-up packet — all before the first call." },
              { n: "03", t: "Document", d: "Photos, signatures, scopes, and timecards roll up into Xactimate-ready packages. Claims get paid faster. Crews get home sooner." },
            ].map((s, i) => (
              <motion.div
                key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="bg-[#0a0a0b] p-10 md:p-14 min-h-[360px] flex flex-col"
              >
                <div className="font-display text-7xl md:text-8xl silver-text mb-8 leading-none">{s.n}</div>
                <div className="font-display text-3xl text-white mb-4">{s.t}</div>
                <p className="font-body text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OPERATORS — first-100 founding callout (no fake testimonials pre-launch) */}
      <section className="relative py-28 md:py-36 border-t border-white/5" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>04 · the operators</SectionLabel>
          <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight max-w-3xl mb-8">
            Built for people who <em className="italic">show up when it counts</em>.
          </h2>
          <p className="font-body text-base text-zinc-400 max-w-2xl mb-14">
            Apex Infinity Ventures is just opening to its founding 100 restoration companies.
            Operator stories live here once those first crews ship their first storms with us.
          </p>
          <div className="border border-white/10 bg-[#121214] p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-[#dc2626] mb-3">
                founding network · 100 seats
              </div>
              <div className="font-display text-3xl md:text-4xl text-white tracking-tight max-w-2xl">
                Be one of the first names on this wall.
              </div>
            </div>
            <a
              href="#join-network"
              className="inline-flex items-center gap-2 btn-primary-apex px-7 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em] whitespace-nowrap"
              data-testid="operators-cta"
            >
              Apply now
            </a>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative py-28 md:py-36 border-t border-white/5" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>05 · access tiers · for contractors</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight max-w-3xl leading-[0.95]">
              Built for crews of every size.
            </h2>
            <p className="font-body text-sm text-zinc-400 max-w-sm">
              Homeowners can use Apex to find a vetted pro free of charge. Restoration companies
              get a single, tailored subscription — contact us for pricing.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: "Independent", tag: "1–5 crews", price: "Starting at $999", cadence: "/ month", features: ["Up to 5 technician seats", "Storm watch + alerts", "Mobile field app", "Most industry software integrations available", "Email support"], highlight: false },
              { name: "Operator", tag: "Most chosen · up to 25 crews", price: "Starting at $3999", cadence: "/ month", features: ["Up to 25 technician seats", "Access to full APEX functions", "AI Sentinel", "Most industry software integrations available", "Priority support · SLA"], highlight: true },
              { name: "Custom", tag: "25+ crews · multi-region", price: "Contact for pricing", cadence: "", features: ["Scaled seating for large enterprises", "Access to full APEX Suite", "AI Sentinel · unlimited", "Carrier API integrations", "Premium Client Support"], highlight: false },
            ].map((p) => (
              <div
                key={p.name}
                className={`relative p-8 border ${p.highlight ? "border-[#dc2626]/60 bg-[#160c0c] shadow-[0_0_60px_rgba(220,38,38,0.15)]" : "border-white/5 bg-[#121214]"} flex flex-col`}
                data-testid={`pricing-tier-${p.name.toLowerCase()}`}
              >
                {p.highlight && (
                  <div className="absolute top-4 right-4 font-mono-apex text-[9px] uppercase tracking-[0.25em] text-[#dc2626] border border-[#dc2626]/40 px-2 py-1">
                    most chosen
                  </div>
                )}
                <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
                  {p.tag}
                </div>
                <div className="font-display text-3xl text-white mb-3">{p.name}</div>
                <div className="mb-8">
                  <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-[#dc2626] mb-1">
                    pricing
                  </div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-2xl silver-text leading-tight">{p.price}</span>
                    {p.cadence && (
                      <span className="font-mono-apex text-xs text-zinc-500">{p.cadence}</span>
                    )}
                  </div>
                </div>
                <ul className="space-y-3 mb-10">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 font-body text-sm text-zinc-300">
                      <Check size={16} className="text-[#dc2626] mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#request-demo"
                  className={`block text-center px-6 py-3 mt-auto font-mono-apex text-[11px] uppercase tracking-[0.25em] ${p.highlight ? "btn-primary-apex" : "btn-secondary-apex"}`}
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                >
                  Contact us
                </a>
              </div>
            ))}
          </div>

          {/* Available add-ons */}
          <div className="mt-6 p-8 md:p-10 border border-white/10 bg-[#121214]" data-testid="pricing-addons">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                  available add-ons
                </div>
                <div className="font-display text-3xl text-white tracking-tight">
                  Bolt on what you need.
                </div>
              </div>
              <p className="font-body text-sm text-zinc-400 max-w-md">
                Available on every tier — folded into your tailored subscription. Contact us for
                pricing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-6 border border-white/5 bg-[#0a0a0b] flex gap-5" data-testid="addon-business-intelligence">
                <img
                  src="/brand/apex-business-intelligence.png?v=1"
                  alt="APEX Business Intelligence"
                  className="w-24 h-24 object-contain shrink-0 border border-white/5 bg-black"
                  loading="lazy"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <span className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      add-on
                    </span>
                    <span className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-[#dc2626]">
                      contact for pricing info
                    </span>
                  </div>
                  <div className="font-display text-xl text-white tracking-tight mb-2">APEX Business Intelligence</div>
                  <p className="font-body text-sm text-zinc-400 leading-relaxed">
                    Apex Intelligence gives contractors and management teams a real-time view of
                    the business through one smart, streamlined dashboard. From performance and
                    operations to financial visibility, it delivers one-click reporting and
                    real-time insight into every dollar moving through your operation.
                  </p>
                </div>
              </div>

              <div className="p-6 border border-white/5 bg-[#0a0a0b] flex gap-5" data-testid="addon-intellivoice">
                <img
                  src="/brand/apex-intellivoice.png?v=2"
                  alt="APEX IntelliVoice Rapid Response"
                  className="w-24 h-24 object-contain shrink-0 border border-white/5 bg-black"
                  loading="lazy"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <span className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      add-on
                    </span>
                    <span className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-[#dc2626]">
                      contact for pricing
                    </span>
                  </div>
                  <div className="font-display text-xl text-white tracking-tight mb-2">APEX IntelliVoice Rapid Response</div>
                  <p className="font-body text-sm text-zinc-400 leading-relaxed">
                    An AI-powered restoration call center that never misses a damage call — day,
                    night, weekends, or holidays. It instantly triages the severity, captures the
                    full property file, and routes emergencies to the nearest available crew in
                    seconds. Faster response. Smarter solutions. Better outcomes. And when a
                    personal touch matters most, a live human is always standing by.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Available add-ons */}
          {/* Homeowner card */}
          <div className="mt-6 p-8 md:p-10 border border-white/5 bg-[#121214] flex flex-col md:flex-row gap-8 items-start md:items-center">
            <div className="w-14 h-14 border border-[#dc2626]/40 bg-[#dc2626]/10 flex items-center justify-center shrink-0">
              <Home size={22} className="text-[#dc2626]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">for homeowners</div>
              <div className="font-display text-3xl text-white mb-2">Find a vetted pro · always free.</div>
              <p className="font-body text-sm text-zinc-400 leading-relaxed">
                If your home was just hit by storm damage, Apex matches you with a certified
                restoration company near you — zero cost, zero obligation. We take a small fee from
                the contractor when work is completed.
              </p>
            </div>
            <a
              href="#find-a-pro"
              className="px-6 py-3 btn-primary-apex font-mono-apex text-[10px] uppercase tracking-[0.25em] whitespace-nowrap"
              data-testid="homeowner-cta"
            >
              Get help now
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="relative py-28 md:py-36 border-t border-white/5" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-start">
          <div>
            <SectionLabel>06 · briefing channel</SectionLabel>
            <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight leading-[0.95] mb-6">
              Tell us what you're up against.
            </h2>
            <p className="font-body text-base text-zinc-400 leading-relaxed mb-10 max-w-md">
              Whether you're a homeowner with damage on the ground or a contractor running 200
              jobs a week, we'll get back within one business day with a tailored response plan.
            </p>
            <div className="space-y-5 font-mono-apex text-xs text-zinc-300">
              <div>
                <div className="text-zinc-500 uppercase tracking-[0.25em] text-[9px] mb-1 flex items-center gap-2"><Clock size={11} /> Emergency line</div>
                24/7 storm response · ops@apexinfinityventures.com
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-[0.25em] text-[9px] mb-1 flex items-center gap-2"><MapPin size={11} /> Headquarters</div>
                Apex Infinity Ventures · United States
              </div>
            </div>
          </div>

          <form onSubmit={submitContact} className="border border-white/5 bg-[#121214] p-8 md:p-10" data-testid="contact-form">
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
              transmit a request
            </div>
            <div className="space-y-5">
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Name</label>
                <input
                  required
                  value={contact.name}
                  onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  className="input-apex w-full px-4 py-3 font-body text-sm"
                  data-testid="contact-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Email</label>
                  <input
                    required type="email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    className="input-apex w-full px-4 py-3 font-body text-sm"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Company / Address</label>
                  <input
                    value={contact.company}
                    onChange={(e) => setContact({ ...contact, company: e.target.value })}
                    className="input-apex w-full px-4 py-3 font-body text-sm"
                    data-testid="contact-company-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">I am a</label>
                  <select
                    value={contact.company_size}
                    onChange={(e) => setContact({ ...contact, company_size: e.target.value })}
                    className="input-apex w-full px-4 py-3 font-body text-sm appearance-none cursor-pointer"
                    data-testid="contact-companysize-input"
                  >
                    <option value="" className="bg-[#0a0a0b]">Select…</option>
                    <option value="Homeowner" className="bg-[#0a0a0b]">Homeowner</option>
                    <option value="Independent contractor" className="bg-[#0a0a0b]">Independent contractor</option>
                    <option value="Restoration company · 1–20 crews" className="bg-[#0a0a0b]">Restoration company · 1–20 crews</option>
                    <option value="Restoration company · 20+ crews" className="bg-[#0a0a0b]">Restoration company · 20+ crews</option>
                    <option value="Insurance carrier / TPA" className="bg-[#0a0a0b]">Insurance carrier / TPA</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">I need help with</label>
                  <select
                    value={contact.intent}
                    onChange={(e) => setContact({ ...contact, intent: e.target.value })}
                    className="input-apex w-full px-4 py-3 font-body text-sm appearance-none cursor-pointer"
                    data-testid="contact-intent-input"
                  >
                    <option value="" className="bg-[#0a0a0b]">Select…</option>
                    <option value="Active damage · need a crew now" className="bg-[#0a0a0b]">Active damage · need a crew now</option>
                    <option value="Evaluating Apex for our team" className="bg-[#0a0a0b]">Evaluating Apex for our team</option>
                    <option value="Replacing existing tools" className="bg-[#0a0a0b]">Replacing existing tools</option>
                    <option value="Partnership / integration" className="bg-[#0a0a0b]">Partnership / integration</option>
                    <option value="Press / media" className="bg-[#0a0a0b]">Press / media</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">What's the situation?</label>
                <textarea
                  required rows={4}
                  value={contact.message}
                  onChange={(e) => setContact({ ...contact, message: e.target.value })}
                  className="input-apex w-full px-4 py-3 font-body text-sm resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              {contactState.error && (
                <div className="font-mono-apex text-xs text-[#dc2626]">{contactState.error}</div>
              )}
              {contactState.ok && (
                <div className="font-mono-apex text-xs text-emerald-400" data-testid="contact-success-msg">
                  Transmission received. We'll respond within one business day.
                </div>
              )}

              <button
                disabled={contactState.loading}
                className="w-full btn-primary-apex px-6 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em] disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {contactState.loading ? "Transmitting…" : "Transmit Request"}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
